import type { FiatEquivalentPrice } from "@/context/FiatPricingProvider";
import type { MinimalCoinBalance } from "@/types";
import { formatUnits } from "ethers";
import type { BalanceSummary, BalanceToken } from "./types";

export function transformBalanceToken(
  coinBalance: MinimalCoinBalance,
  fiatEquivalentPrice?: FiatEquivalentPrice,
  tokenUsdPrice?: number,
): BalanceToken {
  const formattedBalance = formatTokenBalance(
    coinBalance.balance,
    coinBalance.decimals,
  );

  return {
    symbol: coinBalance.symbol,
    name: coinBalance.name || coinBalance.symbol,
    balance: formattedBalance,
    decimals: coinBalance.decimals,
    imageUrl: coinBalance.imageUrl,
    contractAddress: coinBalance.contractAddress,
    fiatEquivalentPrice: fiatEquivalentPrice
      ? {
          amount: fiatEquivalentPrice.amount,
          currency: fiatEquivalentPrice.currency,
          prefixSymbol: fiatEquivalentPrice.prefixSymbol,
        }
      : undefined,
    dailyChange: fiatEquivalentPrice?.usd24HrChange || 0,
    currentPrice: tokenUsdPrice || 0,
  };
}

function formatTokenBalance(balance: string, decimals: number): string {
  try {
    const formattedBalance = formatUnits(balance, decimals);
    const numericBalance = Number.parseFloat(formattedBalance);
    if (numericBalance === 0) {
      return "0";
    }
    if (numericBalance < 0.000001) {
      return "<0.000001";
    }
    if (numericBalance < 1) {
      return numericBalance.toFixed(6).replace(/\.?0+$/, "");
    }
    if (numericBalance < 1000) {
      return numericBalance.toFixed(2).replace(/\.?0+$/, "");
    }
    return numericBalance.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  } catch (error) {
    console.error("Error formatting balance:", error);
    return "0";
  }
}

export function calculatePortfolioSummary(
  tokens: BalanceToken[],
): BalanceSummary | undefined {
  if (!tokens.length) return undefined;

  const tokensWithPrices = tokens.filter((token) => token.fiatEquivalentPrice);
  if (!tokensWithPrices.length) return undefined;

  const totalValue = tokensWithPrices.reduce((sum, token) => {
    if (token.fiatEquivalentPrice) {
      const amount = token.fiatEquivalentPrice.amount.replace(/[$,]/g, "");
      const numericAmount = Number.parseFloat(amount);
      return sum + (Number.isNaN(numericAmount) ? 0 : numericAmount);
    }
    return sum;
  }, 0);

  const avgDailyChange =
    tokensWithPrices.reduce((sum, token) => {
      return sum + (token.dailyChange || 0);
    }, 0) / tokensWithPrices.length;

  const dailyChangeValue = (totalValue * avgDailyChange) / 100;

  return {
    totalValue: totalValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    currency: "$",
    dailyChange: Number.parseFloat(avgDailyChange.toFixed(2)),
    dailyChangeValue: Math.abs(dailyChangeValue).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
}

export function filterNonZeroBalances(tokens: BalanceToken[]): BalanceToken[] {
  return tokens.filter((token) => {
    const balance = token.balance.replace(/,/g, "");
    return Number.parseFloat(balance) > 0;
  });
}
